import { useMemo, useState } from "react";
import type { IAuditLogEntry } from "@dealer-center/ddc-pages-franchise-management-interface";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";

import { useAuditLogEntries } from "@dealer-center/ddc-pages-franchise-management-fetch";
import { getFilteredData } from "./helper";
import {
  Alert,
  Box,
  Card,
  CardContent,
  ClockIcon,
  SearchInput,
  Table,
  Tooltip,
  Typography
} from "@dealer-center/core-components";

const DEFAULT_PAGE_SIZE = 10;

const formatTimeDetail = (dateStr: string): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true
  });
};

const columns: TypeSafeTableColumns<IAuditLogEntry> = [
  {
    dataIndex: "accountId",
    title: "Account",
    columnSortable: true
  },
  {
    dataIndex: "franchiseName",
    title: "Franchise",
    columnSortable: true
  },
  {
    dataIndex: "actionType",
    title: "Action Type",
    columnSortable: true
  },
  {
    dataIndex: "formattedModifiedDate",
    title: "Timestamp",
    columnSortable: true,
    defaultSortOrder: "descend",
    sorter: (recordA: IAuditLogEntry, recordB: IAuditLogEntry) =>
      new Date(recordA.modifiedDate).getTime() -
      new Date(recordB.modifiedDate).getTime(),
    render: (_text: string, record: IAuditLogEntry) => (
      <Tooltip
        position="top"
        size="small"
        sx={{ width: "fit-content", whiteSpace: "nowrap", fontSize: "1rem" }}
        toolTipContent={formatTimeDetail(record.modifiedDate)}
      >
        <span style={{ cursor: "default" }}>
          {record.formattedModifiedDate ?? _text}
        </span>
      </Tooltip>
    )
  },
  {
    dataIndex: "modifiedBy",
    title: "Actor",
    columnSortable: true
  }
];

export const AuditLogTable = () => {
  const { data = [], isLoading, error } = useAuditLogEntries();
  const [filterValue, setFilterValue] = useState("");

  const filteredData = useMemo(() => {
    return getFilteredData(data, filterValue.trim(), columns);
  }, [data, filterValue]);

  if (error) {
    return (
      <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
        {error instanceof Error
          ? error.message
          : "Failed to load audit log data"}
      </Alert>
    );
  }

  return (
    <Card
      content={
        <CardContent>
          <Box sx={{ marginBottom: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 1,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: { xs: 1, sm: 2 }
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 0
                }}
              >
                <ClockIcon
                  style={{
                    fontSize: "20px",
                    marginRight: "8px"
                  }}
                  title="Audit Log"
                />
                <Typography sx={{ fontWeight: "bold" }} variant="h4">
                  Audit Log
                </Typography>
              </Box>

              <SearchInput
                inputValue={filterValue}
                placeholder="Search audit entries..."
                sx={{ maxWidth: "220px" }}
                onInputChange={event =>
                  setFilterValue(event.target.value ?? "")
                }
              />
            </Box>
            <Typography
              sx={{
                color: "text.secondary",
                fontWeight: "normal",
                marginBottom: 2
              }}
              variant="body-xs"
            >
              Track all account franchise changes and modifications
            </Typography>
          </Box>

          {filteredData?.length === 0 && !isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: { xs: "120px", md: "150px" },
                color: "text.secondary",
                textAlign: "center",
                padding: { xs: 2, md: 3 }
              }}
            >
              <Box sx={{ marginBottom: { xs: 1, md: 2 } }}>
                <ClockIcon
                  style={{
                    fontSize: "42px",
                    color: "#9E9E9E"
                  }}
                  title="No audit entries"
                />
              </Box>
              <Typography
                sx={{
                  marginBottom: 1,
                  fontWeight: "medium",
                  fontSize: { xs: "0.875rem", md: "1rem" }
                }}
                variant="body-sm"
              >
                {data?.length === 0
                  ? "No audit entries yet"
                  : "No matching entries found"}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.75rem", md: "0.875rem" }
                }}
                variant="body-xs"
              >
                {data?.length === 0
                  ? "Changes will appear here when account franchises are created or modified"
                  : "Try adjusting your search terms"}
              </Typography>
            </Box>
          ) : (
            <Table.Table
              sortableColumns
              borderType="contained"
              columns={columns}
              data={filteredData || []}
              defaultPageSize={DEFAULT_PAGE_SIZE}
              isLoading={isLoading}
            />
          )}
        </CardContent>
      }
    />
  );
};
